/**
 * 店舗一覧: 地図モーダル
 *
 * - モーダルの開閉（エリア選択オーバーレイ含む）
 * - Google マップの初期化（初回オープン時に遅延ロード）
 * - 店舗マーカー（Figma 697:30292 のピンデザイン、店舗 ID を表示）
 * - マーカークリックでサイドバーカードを絞り込み
 * - エリア選択で該当エリアの店舗に地図・カードを絞り込み
 * - カードクリックで店舗詳細ページへ遷移（お問い合わせ / 来店予約ボタンは各フォームへ）
 *
 * 【データ受け渡し】
 *   ルート要素の data-shops に店舗データ（JSON）を持つ。
 *   API キー / マップ ID は meta タグ（google-api-key / google-map-id）から取得。
 */

import { setOptions, importLibrary } from "@googlemaps/js-api-loader";

// fitBounds 後の最大ズーム（1 店舗のみの場合に寄りすぎるのを防ぐ）
const MAX_FIT_ZOOM = 15;

/**
 * data-shops の JSON をパースする（不正な JSON でも画面を落とさない）
 *
 * @param {HTMLElement} modal - ルート要素
 * @returns {Array<Object>} 店舗データ配列
 */
const parseShops = (modal) => {
    try {
        return JSON.parse(modal.dataset.shops || "[]");
    } catch (e) {
        return [];
    }
};

/**
 * マップピン要素を生成する（店舗 ID を表示）
 *
 * @param {number|string} label - ピンに表示する数字
 * @returns {HTMLElement}
 */
const createPinElement = (label) => {
    const pin = document.createElement("div");
    pin.className = "c-store-map-modal__gmap-pin";
    pin.textContent = String(label);
    return pin;
};

const storeMapModal = () => {
    const modal = document.querySelector('[data-store-map-modal]');
    if (!modal) return;

    const prefPicker = modal.querySelector('[data-store-map-pref-picker]');
    const openers = document.querySelectorAll('[data-store-map-modal-open]');
    const closers = modal.querySelectorAll('[data-store-map-modal-close]');
    const prefClosers = modal.querySelectorAll('[data-store-map-pref-close]');
    const prefPicks = modal.querySelectorAll('[data-store-map-pref-pick]');
    const sidebarToggles = modal.querySelectorAll('[data-store-map-sidebar-toggle]');
    const cards = Array.from(modal.querySelectorAll('[data-store-map-card]'));
    let lastFocus = null;

    // ── サイドバー（店舗カードメニュー）の開閉 ──

    /**
     * サイドバーの開閉状態を切り替える。
     * ルート要素の data-sidebar-closed で SCSS 側の表示を制御し、
     * トグルボタンのラベルも合わせて更新する。
     *
     * @param {boolean} closed - true で閉じる
     */
    const setSidebarClosed = (closed) => {
        modal.toggleAttribute('data-sidebar-closed', closed);
        sidebarToggles.forEach((el) => {
            const label = el.querySelector('.c-store-map-modal__sidebar-toggle-label');
            if (label) label.textContent = closed ? '開く' : '閉じる';
        });
    };

    sidebarToggles.forEach((el) => {
        el.addEventListener('click', () => {
            setSidebarClosed(!modal.hasAttribute('data-sidebar-closed'));
        });
    });

    // ── サイドバーカード ──

    /**
     * 表示するカードを店舗 ID で絞り込む。
     * サイドバーが閉じていた場合は絞り込み結果を見せるため開き直す。
     *
     * @param {Array<number>|null} shopIds - 表示する店舗 ID。null で全件表示
     */
    const showCards = (shopIds) => {
        cards.forEach((card) => {
            const visible =
                shopIds === null || shopIds.includes(Number(card.dataset.shopId));
            card.toggleAttribute('data-hidden', !visible);
        });
        setSidebarClosed(false);
    };

    // カードクリックで店舗詳細ページへ（カード内のリンククリックは除外）
    cards.forEach((card) => {
        card.addEventListener('click', (e) => {
            if (e.target.closest('a')) return;
            window.location.href = card.dataset.detailHref;
        });
    });

    // ── Google マップ ──

    let map = null;
    let markers = [];
    let mapInitStarted = false;

    /**
     * 対象マーカー全体が収まるように表示範囲を調整する。
     * 1 店舗のみ等で寄りすぎる場合はズームを制限する。
     *
     * @param {Array<google.maps.marker.AdvancedMarkerElement>} targets
     */
    const fitToMarkers = (targets) => {
        if (!map || targets.length === 0) return;
        const bounds = new google.maps.LatLngBounds();
        targets.forEach((marker) => bounds.extend(marker.position));
        map.fitBounds(bounds);
        google.maps.event.addListenerOnce(map, 'idle', () => {
            if (map.getZoom() > MAX_FIT_ZOOM) map.setZoom(MAX_FIT_ZOOM);
        });
    };

    /**
     * Google マップを初期化し、店舗マーカーを配置する。
     * API キー未設定・店舗データ無しの場合は何もしない。
     */
    const initMap = async () => {
        const canvas = modal.querySelector('[data-store-map-canvas]');
        const apiKey = document.querySelector('meta[name="google-api-key"]')?.content;
        const mapId = document.querySelector('meta[name="google-map-id"]')?.content;
        const shops = parseShops(modal);
        if (!canvas || !apiKey || !mapId || shops.length === 0) return;

        setOptions({
            key: apiKey,
            version: 'weekly',
        });

        const { Map } = await importLibrary('maps');
        const { AdvancedMarkerElement } = await importLibrary('marker');

        map = new Map(canvas, {
            mapId,
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: false,
        });

        // 店舗マーカーを配置（クリックで該当カードのみ表示）
        markers = shops.map((shop) => {
            const marker = new AdvancedMarkerElement({
                position: { lat: shop.lat, lng: shop.lng },
                map,
                content: createPinElement(shop.id),
            });
            marker.shopId = shop.id;
            marker.areaId = shop.area_id;
            marker.addListener('gmp-click', () => {
                showCards([shop.id]);
            });
            return marker;
        });

        // 全店舗が収まる表示範囲で初期化
        fitToMarkers(markers);
    };

    // ── モーダル開閉 ──

    const closePrefOnly = () => {
        modal.classList.remove('is-store-map-modal--pref-open');
        if (prefPicker) {
            prefPicker.setAttribute('aria-hidden', 'true');
        }
    };

    const open = (withPrefPicker) => {
        lastFocus = document.activeElement;
        modal.classList.add('is-open');
        modal.setAttribute('aria-hidden', 'false');
        document.body.classList.add('is-store-map-modal-open');

        // 初回オープン時のみマップを初期化（非表示中は描画できないため遅延ロード）
        if (!mapInitStarted) {
            mapInitStarted = true;
            initMap();
        }

        if (withPrefPicker && prefPicker) {
            modal.classList.add('is-store-map-modal--pref-open');
            prefPicker.setAttribute('aria-hidden', 'false');
        } else {
            closePrefOnly();
        }

        let toFocus = modal.querySelector('[data-store-map-modal-close]');
        if (withPrefPicker && prefPicker) {
            const narrow = typeof window.matchMedia === 'function'
                && window.matchMedia('(max-width: 768px)').matches;
            const inPref = narrow
                ? prefPicker.querySelector('.c-store-map-modal__pref-outside-close')
                : prefPicker.querySelector('.c-store-map-modal__pref-dialog-close');
            if (inPref) toFocus = inPref;
        }
        toFocus?.focus({ preventScroll: true });
    };

    const close = () => {
        closePrefOnly();
        modal.classList.remove('is-open');
        modal.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('is-store-map-modal-open');
        if (lastFocus && typeof lastFocus.focus === 'function') {
            lastFocus.focus({ preventScroll: true });
        }
    };

    openers.forEach((el) => {
        el.addEventListener('click', (e) => {
            e.preventDefault();
            open(el.hasAttribute('data-store-map-pref-picker'));
        });
    });

    closers.forEach((el) => {
        el.addEventListener('click', () => close());
    });

    prefClosers.forEach((el) => {
        el.addEventListener('click', () => closePrefOnly());
    });

    // エリア選択: 該当エリアの店舗に地図・カードを絞り込む
    prefPicks.forEach((el) => {
        el.addEventListener('click', (e) => {
            e.preventDefault();
            closePrefOnly();

            const areaId = el.dataset.areaId;
            if (!areaId) return;

            const areaMarkers = markers.filter(
                (marker) => String(marker.areaId) === String(areaId),
            );
            if (areaMarkers.length === 0) {
                // 該当エリアに店舗が無い場合は全件表示に戻す
                showCards(null);
                return;
            }

            showCards(areaMarkers.map((marker) => marker.shopId));
            fitToMarkers(areaMarkers);
        });
    });

    document.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape') return;
        if (!modal.classList.contains('is-open')) return;
        if (modal.classList.contains('is-store-map-modal--pref-open')) {
            closePrefOnly();
            return;
        }
        close();
    });
};

export default storeMapModal;
